import { useEffect, useState } from 'react';
import { TriangleAlert } from 'lucide-react';
import {
  getApiError,
  useCreateEventMutation,
  useUpdateEventMutation,
  type EventCategory,
  type Group,
  type SchoolEvent,
} from '@/store/api';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { EVENT_CATEGORY_LABELS, isoToLocalInput, localInputToIso } from '@/lib/datetime';
import { OFFSET_LABELS } from './RemindersDialog';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

/*
  The event editor.

  ── Why it never publishes ─────────────────────────────────────────────────

  Saving writes a draft. Publishing is a separate button on the list, because
  publishing is the moment every parent in the school is told — and a dialog
  that saves and announces in one click makes a half-finished sports day an
  announcement by accident.

  ── Why the audience is checkboxes and not a multi-select ──────────────────

  A native multi-select requires ctrl-click to pick a second option, which a
  school secretary should not have to know. Checkboxes also let "All groups"
  read as what it is: the default, with nothing ticked.
*/

export interface EventDraft {
  _id?: string;
  title: string;
  description: string;
  category: EventCategory;
  startAt: string;
  endAt: string;
  allDay: boolean;
  location: string;
  groupIds: string[];
  rsvpRequired: boolean;
  rsvpDeadline: string;
  reminderOffsetsHours: number[];
}

export function emptyEventDraft(): EventDraft {
  /* Default to the next whole hour two weeks out — far enough that a school can
     actually announce it, and a round time nobody has to edit. */
  const start = new Date();
  start.setDate(start.getDate() + 14);
  start.setHours(9, 0, 0, 0);
  const end = new Date(start);
  end.setHours(12, 0, 0, 0);

  return {
    title: '',
    description: '',
    category: 'other',
    startAt: isoToLocalInput(start.toISOString()),
    endAt: isoToLocalInput(end.toISOString()),
    allDay: false,
    location: '',
    groupIds: [],
    rsvpRequired: false,
    rsvpDeadline: '',
    reminderOffsetsHours: [],
  };
}

export function toEventDraft(event: SchoolEvent): EventDraft {
  return {
    _id: event._id,
    title: event.title,
    description: event.description,
    category: event.category,
    startAt: isoToLocalInput(event.startAt),
    endAt: isoToLocalInput(event.endAt),
    allDay: event.allDay,
    location: event.location,
    groupIds: event.groupIds,
    rsvpRequired: event.rsvpRequired,
    rsvpDeadline: isoToLocalInput(event.rsvpDeadline),
    reminderOffsetsHours: event.reminderOffsetsHours ?? [],
  };
}

export function EventDialog({
  draft,
  groups,
  institutionId,
  onClose,
}: {
  draft: EventDraft | null;
  groups: Group[];
  institutionId: string;
  onClose: () => void;
}) {
  const [form, setForm] = useState<EventDraft>(draft ?? emptyEventDraft());
  const [error, setError] = useState('');

  const [createEvent, createState] = useCreateEventMutation();
  const [updateEvent, updateState] = useUpdateEventMutation();
  const saving = createState.isLoading || updateState.isLoading;
  const isEditing = Boolean(form._id);

  useEffect(() => {
    if (draft) setForm(draft);
    setError('');
  }, [draft]);

  const toggleGroup = (id: string) =>
    setForm((f) => ({
      ...f,
      groupIds: f.groupIds.includes(id) ? f.groupIds.filter((g) => g !== id) : [...f.groupIds, id],
    }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.title.trim()) return setError('Give the event a title.');

    const startAt = localInputToIso(form.startAt);
    const endAt = localInputToIso(form.endAt);
    if (!startAt || !endAt) return setError('Set when the event starts and ends.');
    if (new Date(endAt) < new Date(startAt)) return setError('The event cannot end before it starts.');

    const rsvpDeadline = form.rsvpRequired ? localInputToIso(form.rsvpDeadline) : null;
    if (rsvpDeadline && new Date(rsvpDeadline) > new Date(startAt)) {
      return setError('Replies have to close on or before the event starts.');
    }

    const data = {
      title: form.title.trim(),
      description: form.description.trim() || undefined,
      category: form.category,
      startAt,
      endAt,
      allDay: form.allDay,
      location: form.location.trim() || undefined,
      groupIds: form.groupIds,
      rsvpRequired: form.rsvpRequired,
      rsvpDeadline: rsvpDeadline ?? undefined,
      reminderOffsetsHours: form.reminderOffsetsHours,
    };

    try {
      if (form._id) {
        await updateEvent({ institutionId, eventId: form._id, data }).unwrap();
      } else {
        await createEvent({ institutionId, data }).unwrap();
      }
      onClose();
    } catch (err) {
      setError(getApiError(err));
    }
  };

  return (
    <Modal
      open={draft !== null}
      onClose={onClose}
      size="lg"
      title={isEditing ? 'Edit event' : 'New event'}
      description="Saved as a draft. Nobody sees it until you publish it from the list."
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button form="event-form" type="submit" loading={saving}>
            {isEditing ? 'Save changes' : 'Save draft'}
          </Button>
        </div>
      }
    >
      <form id="event-form" onSubmit={handleSubmit} className="flex flex-col gap-3">
        {error && (
          <div className={cn(styles.markDanger, 'w-full justify-start normal-case tracking-normal text-[11px]')}>
            <TriangleAlert size={12} className="flex-shrink-0" />
            {error}
          </div>
        )}

        <Field label="Title" required>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Annual Sports Day"
            autoFocus
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Category">
            <Select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value as EventCategory })}
            >
              {Object.entries(EVENT_CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Location">
            <Input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Main grounds"
            />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Starts" required>
            <Input
              type="datetime-local"
              value={form.startAt}
              onChange={(e) => setForm({ ...form, startAt: e.target.value })}
            />
          </Field>
          <Field label="Ends" required>
            <Input
              type="datetime-local"
              value={form.endAt}
              onChange={(e) => setForm({ ...form, endAt: e.target.value })}
            />
          </Field>
        </div>

        <label className="flex items-center gap-2 text-[11.5px] font-medium text-foreground">
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={form.allDay}
            onChange={(e) => setForm({ ...form, allDay: e.target.checked })}
          />
          All day — show the date without times
        </label>

        <Field label="Description" hint="Parents see this">
          <textarea
            className={cn(styles.input, 'min-h-[68px] resize-y')}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What it is, what to bring, where to meet."
          />
        </Field>

        <div>
          <p className={cn(styles.label, 'mb-1.5')}>Who it is for</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {groups.map((g) => (
              <label key={g._id} className="flex items-center gap-1.5 text-[11.5px] text-foreground">
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={form.groupIds.includes(g._id)}
                  onChange={() => toggleGroup(g._id)}
                />
                {g.name}
              </label>
            ))}
          </div>
          <p className={cn(styles.meta, 'mt-1.5')}>
            {form.groupIds.length === 0
              ? 'Nothing ticked — this goes to the whole school.'
              : `${form.groupIds.length} group${form.groupIds.length === 1 ? '' : 's'} selected.`}
          </p>
        </div>

        <div className={cn(styles.panel, 'flex flex-col gap-2.5 p-3')}>
          <label className="flex items-center gap-2 text-[11.5px] font-semibold text-foreground">
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={form.rsvpRequired}
              onChange={(e) => setForm({ ...form, rsvpRequired: e.target.checked })}
            />
            Ask parents whether their child is coming
          </label>

          {form.rsvpRequired && (
            <Field label="Replies close" hint="Defaults to when the event starts">
              <Input
                type="datetime-local"
                value={form.rsvpDeadline}
                onChange={(e) => setForm({ ...form, rsvpDeadline: e.target.value })}
              />
            </Field>
          )}
        </div>

        <div className={cn(styles.panel, 'flex flex-col gap-2 p-3')}>
          <p className={styles.sectionTitle}>Remind parents by SMS</p>
          <p className={styles.meta}>
            One message per phone, however many children they have here. Nothing is sent until the
            event is published — and reminder sending has its own server-wide switch.
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-0.5">
            {[168, 72, 48, 24, 2].map((hours) => (
              <label key={hours} className="flex items-center gap-1.5 text-[11.5px] text-foreground">
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={form.reminderOffsetsHours.includes(hours)}
                  onChange={() =>
                    setForm((f) => ({
                      ...f,
                      reminderOffsetsHours: f.reminderOffsetsHours.includes(hours)
                        ? f.reminderOffsetsHours.filter((h) => h !== hours)
                        : [...f.reminderOffsetsHours, hours],
                    }))
                  }
                />
                {OFFSET_LABELS[hours]}
              </label>
            ))}
          </div>
          {form.reminderOffsetsHours.length === 0 && (
            <p className={styles.meta}>Nothing ticked — no reminders for this event.</p>
          )}
        </div>
      </form>
    </Modal>
  );
}
